import * as Sentry from "@sentry/react"
import { getUserEmail, getUserId } from "helpers/local_storage"

/**
 * 5.2.2 Traceable Error Codes (TRC)
 * Format: TRC-[CATEGORY]-[BASE36_TIMESTAMP]-[4_CHAR_RANDOM]
 * Example: TRC-DEC-M8KV2-A3FX
 */
export type TRCCategory = "DEC" | "KEY" | "UPL" | "SES" | "SHR" | "DWL"

export const generateTRCCode = (category: TRCCategory): string => {
  const ts = Math.floor(Date.now() / 1000)
    .toString(36)
    .toUpperCase()
    .slice(-5)
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
  const rand = Array.from(
    { length: 4 },
    () => chars[Math.floor(Math.random() * chars.length)]
  ).join("")
  return `TRC-${category}-${ts}-${rand}`
}

/** Reads user context from localStorage — spread into every Sentry payload */
export const getSentryUserContext = (): {
  user_id: string
  user_email: string
  page_url: string
} => ({
  user_id: getUserId() || "",
  user_email: getUserEmail() || "",
  page_url: window.location.href,
})

export type SentryBreadcrumbCategory =
  | "key-health"
  | "key-recovery"
  | "share"
  | "download"
  | "decrypt"

export const addBreadcrumb = (
  category: SentryBreadcrumbCategory,
  message: string,
  level: Sentry.SeverityLevel = "info",
  data?: Record<string, unknown>
): void => {
  Sentry.addBreadcrumb({ category, message, level, data })
}

/**
 * 5.2.1 Standard Log Structure
 * All fields are non-sensitive. Key values, encrypted blobs, and pre-signed
 * URLs must never be included.
 */
export interface SentryEncryptionPayload {
  error_category: TRCCategory
  error_type: string
  page_url: string
  user_id: string
  user_email: string
  error_code: string
  step_trail: string
  step_failed?: string
  key_type?: string
  encryption_mode?: string
  keys_failed?: number
  keys_total?: number
  /** File name only — never the URL */
  file_name?: string
  file_url?: string
  file_type?: string
  build_id?: string
  key_index?: number
  conversation_code?: string
  key_path_tried?: string
  [key: string]: unknown
}

// SHR — sharing/invitation errors
export interface SentrySharePayload {
  error_category: "SHR"
  error_type: string
  page_url: string
  user_id: string
  user_email: string
  error_code: string
  step_trail: string
  step_failed?: string
  project_id?: string
  invitee_user_id?: string
  build_id?: string
  entity_id?: string
  entity_name?: string
  conversation_code?: string
  conversation_codes?: string[]
  [key: string]: unknown
}

// DWL — download queue errors
export interface SentryDownloadPayload {
  error_category: "DWL"
  error_type: string
  page_url: string
  user_id: string
  user_email: string
  error_code: string
  step_trail: string
  step_failed?: string
  file_name?: string
  file_url?: string
  build_id?: string
  download_id?: string
  http_status?: number
  file_index?: number
  skipped_files?: string[]
  [key: string]: unknown
}

// KEY — key recovery / health check events
export interface SentryKeyPayload {
  error_category: "KEY"
  error_type: string
  page_url: string
  user_id: string
  user_email: string
  error_code: string
  step_trail: string
  step_failed?: string
  key_type?: "project" | "build" | "conversation" | "vault" | "private"
  keys_failed?: number
  keys_total?: number
  failed_keys?: { id: string; reason: string }[]
  [key: string]: unknown
}

export const captureSentryEncryptionEvent = (
  message: string,
  payload: SentryEncryptionPayload
): void => {
  Sentry.captureMessage(message, {
    level: "error",
    tags: {
      error_code: payload.error_code,
      error_category: payload.error_category,
      error_type: payload.error_type,
    },
    extra: payload,
  })
}

// SHR — supports both warning and error level
export const captureShareEvent = (
  message: string,
  payload: SentrySharePayload,
  level: Sentry.SeverityLevel = "error"
): void => {
  Sentry.captureMessage(message, {
    level,
    tags: {
      error_code: payload.error_code,
      error_category: payload.error_category,
      error_type: payload.error_type,
    },
    extra: payload,
  })
}

// DWL
export const captureDownloadEvent = (
  message: string,
  payload: SentryDownloadPayload
): void => {
  Sentry.captureException(new Error(message), {
    level: "error",
    tags: {
      error_code: payload.error_code,
      error_category: payload.error_category,
      error_type: payload.error_type,
    },
    extra: payload,
  })
}

// KEY
export const captureKeyEvent = (
  message: string,
  payload: SentryKeyPayload
): void => {
  Sentry.captureMessage(message, {
    level: "error",
    tags: {
      error_code: payload.error_code,
      error_category: payload.error_category,
      error_type: payload.error_type,
    },
    extra: payload,
  })
}
